import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface BusinessDocumentPipelineOption {
  value: string;
  label: string;
  stepCount?: number;
  documentTypeKey?: string;
}

@Component({
  selector: 'app-business-document-pipeline-picker',
  standalone: false,
  templateUrl: './business-document-pipeline-picker.html',
  styleUrl: './business-document-pipeline-picker.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => BusinessDocumentPipelinePicker),
    multi: true,
  }],
})
export class BusinessDocumentPipelinePicker implements ControlValueAccessor {
  @Input() options: BusinessDocumentPipelineOption[] = [];
  @Input() value = '';
  @Input() disabled = false;
  @Input() ariaLabel = 'Select pipeline';
  @Input() stepCount?: number;
  @Input() documentTypeKey?: string;
  @Output() valueChange = new EventEmitter<string>();

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get filteredOptions(): BusinessDocumentPipelineOption[] {
    return this.options.filter(option =>
      (this.stepCount === undefined || option.stepCount === this.stepCount)
      && (this.documentTypeKey === undefined || option.documentTypeKey === this.documentTypeKey));
  }

  get selectionUnavailable(): boolean {
    return !!this.value && !this.filteredOptions.some(option => option.value === this.value);
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(callback: (value: string) => void): void {
    this.onChange = callback;
  }

  registerOnTouched(callback: () => void): void {
    this.onTouched = callback;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  choose(value: string): void {
    if (this.disabled || !this.filteredOptions.some(option => option.value === value)) return;
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  touch(): void {
    this.onTouched();
  }
}
